import { reactWindowService } from '@approvalmax/utils';
import { memo, useCallback, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

import { BrowserNewWindowProps } from './BrowserNewWindow.types';

export const BrowserNewWindow = memo<BrowserNewWindowProps>((props) => {
    const { title, children, onClose } = props;

    const browserWindow = useRef<Window | null>(null);
    const browserWindowContainer = useRef<HTMLDivElement | null>(null);

    const handleConnectExternalWindow = useCallback(
        (windowExternal: Window) => {
            browserWindow.current = windowExternal;

            if (!browserWindow.current) {
                return;
            }

            if (title) {
                browserWindow.current.document.title = title;
            }

            browserWindowContainer.current = browserWindow.current.document.createElement('div');
            browserWindow.current.document.body.appendChild(browserWindowContainer.current);

            browserWindow.current.addEventListener('beforeunload', () => {
                onClose && onClose();
            });
        },
        [onClose, title]
    );

    const handleWillMount = useCallback(() => {
        const externalWindow = reactWindowService.getNewWindow();

        if (externalWindow) {
            handleConnectExternalWindow(externalWindow);
        } else {
            console.warn(
                'NewWindow component has been mounted without an external window ' +
                    'manually opened via reactWindowService.openNewWindow().\n' +
                    'You don`t probably want that.\n' +
                    'Reasons for manual opening: \n' +
                    '1. AdBlock plugins might block any windows opened outside user-initiated events (like clicks).\n' +
                    '2. AdBlock plugins might block any window.print() calls outside the code initiated by that window.\n' +
                    'Rules:\n' +
                    '1. Open a new window when handling a user click event.\n' +
                    '2. Call window.print inside the code initiated by onLoad() method ' +
                    'of the window (provided by reactWindowService.openNewWindow())'
            );
        }
    }, [handleConnectExternalWindow]);

    const handleUnmount = () => {
        reactWindowService.closeNewWindow();
    };

    useLayoutEffect(() => {
        handleWillMount();

        return () => {
            handleUnmount();
        };
    }, [handleWillMount]);

    return browserWindowContainer.current ? ReactDOM.createPortal(children, browserWindowContainer.current) : null;
});

BrowserNewWindow.displayName = 'BrowserNewWindow';
